import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import colors from '../../styles/colors';

const PROJECT_MANAGERS = [
  'Adolfo Alvarado',
  'Andy Hercock',
  'Ralph Simmons',
  'Jeff Oxford',
  'Josh Watson',
  'Matteo Petroncelli',
  'Patrick Guevara',
  'Patrick Kabui',
  'Scott McGinn',
  'Zoe James',
];

const SERVICE_TYPES = [
  'SEO',
  'Link Building: If you want links built',
  'AI SEO: For inclusion of brand mentions, press releases',
  'Content: If including page optimizations, blog posts etc',
];

const AI_SEO_PLAN_TYPES = [
  'Core (~$2,400)',
  'Accelerator (~$4,900)',
  'Not Applicable',
];

const FEE_TYPES = [
  'Ongoing Monthly Retainer',
  'One-off Cost for Links',
  'Monthly Fee + One-off Cost for Links',
];

const MARKETS = ['USA', 'Canada', 'UK', 'Europe', 'Australia', 'Global'];

const INITIAL_FORM = {
  'Company Name': '',
  'Business Address': '',
  'First Name': '',
  'Last Name': '',
  'Client Email': '',
  'Website URL': '',
  'Website Blog URL': '',
  'Start Date': '',
  'Project Manager': '',
  'Service Type/s': [],
  'AI SEO Plan Type': '',
  'Fee Type': '',
  'Monthly Fee': '',
  'Primary Market/Region': 'USA',
  'Secondary Market/Region/s': [],
  'They sell:': '',
  'Start Campaign Quota Date': '',
  'Monthly Campaign Quota': '',
  'Outreach Emails Per Weekday': '',
  'Monthly Page Optimizations': '',
  'Monthly Blog Posts': '',
  'Reddit Quota': '',
  Notes: '',
};

const STEPS = [
  {
    id: 'client',
    nav: 'Client Information',
    kicker: 'Section 1',
    title: 'Client Information',
    description: 'Basic client and account ownership information.',
    fields: [
      'Company Name',
      'Business Address',
      'First Name',
      'Last Name',
      'Client Email',
      'Website URL',
      'Website Blog URL',
      'Start Date',
      'Project Manager',
    ],
  },
  {
    id: 'services',
    nav: 'Services & Commercial',
    kicker: 'Section 2',
    title: 'Services & Commercial Details',
    description:
      "Define the client's services, commercial arrangement, target markets and business details.",
    fields: [
      'Service Type/s',
      'AI SEO Plan Type',
      'Fee Type',
      'Primary Market/Region',
      'They sell:',
    ],
  },
  {
    id: 'quotas',
    nav: 'Monthly Quota/s',
    kicker: 'Section 3',
    title: 'Monthly Quota/s',
    description: 'Enter the agreed campaign, content, outreach and Reddit quotas.',
    fields: [
      'Start Campaign Quota Date',
      'Monthly Campaign Quota',
      'Outreach Emails Per Weekday',
      'Monthly Page Optimizations',
      'Monthly Blog Posts',
      'Reddit Quota',
    ],
  },
  {
    id: 'clickup',
    nav: 'Deliverables',
    kicker: 'Section 4',
    title: 'Deliverables',
    description:
      'Search and add the monthly deliverable tasks to create for this client. Deadlines are measured in weeks after form submission.',
    fields: [],
  },
  {
    id: 'extra',
    nav: 'Additional Info',
    kicker: 'Section 5',
    title: 'Additional Information',
    description: 'Add any special requirements, exceptions or context the team should know.',
    fields: ['Notes'],
  },
];

function parentKey(category, taskName) {
  return `${category}::${taskName}`;
}

function subKey(category, taskName, subName) {
  return `${parentKey(category, taskName)}::${subName}`;
}

const GANTT_WEEKS = 24;
const GANTT_MONTHS = ['Month 1', 'Month 2', 'Month 3', 'Month 4', 'Month 5', 'Month 6'];

function clampWeek(n) {
  return Math.max(1, Math.min(GANTT_WEEKS, Number(n)));
}

function ganttSpan(item) {
  const rec = item?.recurrence || {};
  const startRaw = item?.start_week_after_submission ?? rec.active_from_week ?? null;
  const due = item?.deadline_weeks_after_submission;
  const endRaw = rec.active_through_week ?? (typeof due === 'number' ? due : null);

  if (Array.isArray(due)) {
    return { type: 'markers', weeks: due.map(clampWeek) };
  }
  if (startRaw != null && endRaw != null) {
    return {
      type: 'bar',
      start: clampWeek(startRaw),
      end: clampWeek(endRaw),
      recurring: Boolean(item.recurring),
    };
  }
  if (typeof due === 'number') {
    return { type: 'markers', weeks: [clampWeek(due)] };
  }
  if (startRaw != null) {
    return {
      type: 'bar',
      start: clampWeek(startRaw),
      end: GANTT_WEEKS,
      recurring: Boolean(item.recurring),
    };
  }
  return { type: 'none' };
}

function filledWeeks(span) {
  const weeks = new Set();
  if (span.type === 'bar') {
    for (let week = span.start; week <= span.end; week += 1) weeks.add(week);
  } else if (span.type === 'markers') {
    span.weeks.forEach((week) => weeks.add(week));
  }
  return weeks;
}

function formatDue(value) {
  if (value == null) return 'No Deadline Available';
  if (Array.isArray(value)) {
    return `Due weeks ${value.join(', ')} after submission`;
  }
  if (value === 1) return 'Due 1 week after submission';
  return `Due ${value} weeks after submission`;
}

function formatStart(value) {
  if (value == null) return null;
  if (value === 1) return 'Starts 1 week after submission';
  return `Starts week ${value} after submission`;
}

function formatRecurrence(item) {
  if (!item?.recurring) return null;
  const rec = item.recurrence || {};
  const bits = [rec.frequency ? `Recurring ${rec.frequency}` : 'Recurring'];
  if (rec.months) bits.push(`${rec.months}-month window`);
  if (rec.active_from_week != null && rec.active_through_week != null) {
    bits.push(`active weeks ${rec.active_from_week}–${rec.active_through_week}`);
  }
  return bits.join(' · ');
}

function deadlineLines(item) {
  return [formatDue(item?.deadline_weeks_after_submission), formatStart(item?.start_week_after_submission), formatRecurrence(item)].filter(Boolean);
}

function DeadlineMeta({ item, compact }) {
  const lines = deadlineLines(item);
  if (compact) {
    return <span className="co-option-meta">{lines.join(' · ')}</span>;
  }
  return (
    <div className="co-dl">
      {lines.map((line) => (
        <span key={line}>{line}</span>
      ))}
    </div>
  );
}

function formatApiErrors(data) {
  if (!data) return 'Could not submit the form. Please try again.';
  if (typeof data === 'string') return data;
  if (typeof data.detail === 'string') return data.detail;
  const parts = [];
  const walk = (obj) => {
    if (Array.isArray(obj)) {
      obj.forEach((item) => {
        if (typeof item === 'string') parts.push(item);
        else walk(item);
      });
    } else if (obj && typeof obj === 'object') {
      Object.values(obj).forEach(walk);
    }
  };
  walk(data);
  return parts.join(' ') || 'Could not submit the form. Please try again.';
}

function isEmpty(value) {
  return Array.isArray(value) ? value.length === 0 : !String(value || '').trim();
}

function fitCanvasText(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let value = text;
  while (value.length > 1 && ctx.measureText(`${value}…`).width > maxWidth) {
    value = value.slice(0, -1);
  }
  return `${value}…`;
}

function flattenGanttRows(groups) {
  const rows = [];
  groups.forEach((group) => {
    rows.push({ kind: 'category', label: group.category });
    group.tasks.forEach(({ task, subtasks }) => {
      rows.push({ kind: 'task', label: task.task_name, item: task });
      subtasks.forEach((sub) => {
        rows.push({ kind: 'sub', label: sub.task_name, item: sub });
      });
    });
  });
  return rows;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

async function exportGanttJpg(groups) {
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      /* continue with fallback fonts */
    }
  }

  const rows = flattenGanttRows(groups);
  const labelW = 240;
  const weekW = 30;
  const rowH = 34;
  const catH = 30;
  const headH = 52;
  const pad = 24;
  const legendH = 44;
  const chartW = labelW + GANTT_WEEKS * weekW;
  const bodyH = rows.reduce((sum, row) => sum + (row.kind === 'category' ? catH : rowH), 0);
  const width = pad * 2 + chartW;
  const height = pad * 2 + headH + bodyH + legendH;
  const scale = 2;

  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(width * scale);
  canvas.height = Math.ceil(height * scale);
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const bg = colors.marketing.background;
  const headerBg = colors.marketing.greenSoft;
  const catBg = colors.marketing.greenSoft;
  const fill = colors.marketing.green;
  const grid = colors.marketing.borderGrey;
  const gridStrong = colors.marketing.borderDark;
  const text = colors.marketing.deepCharcoal;
  const muted = colors.marketing.greyLight;

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, width, height);

  const originX = pad;
  const originY = pad;
  const font = '"Open Sans", Arial, sans-serif';

  ctx.fillStyle = headerBg;
  ctx.fillRect(originX, originY, chartW, headH);

  ctx.fillStyle = text;
  ctx.font = `700 12px ${font}`;
  ctx.textBaseline = 'middle';
  ctx.fillText('6-month timeline', originX + 12, originY + headH / 2);

  ctx.font = `700 11px ${font}`;
  ctx.textAlign = 'center';
  GANTT_MONTHS.forEach((month, i) => {
    const x = originX + labelW + i * 4 * weekW + 2 * weekW;
    ctx.fillText(month.toUpperCase(), x, originY + 16);
  });

  ctx.font = `400 10px ${font}`;
  ctx.fillStyle = muted;
  for (let week = 1; week <= GANTT_WEEKS; week += 1) {
    const x = originX + labelW + (week - 0.5) * weekW;
    ctx.fillText(String(week), x, originY + 36);
  }

  ctx.strokeStyle = gridStrong;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(originX + labelW, originY);
  ctx.lineTo(originX + labelW, originY + headH + bodyH);
  ctx.stroke();

  let y = originY + headH;
  rows.forEach((row) => {
    if (row.kind === 'category') {
      ctx.fillStyle = catBg;
      ctx.fillRect(originX, y, chartW, catH);
      ctx.fillStyle = fill;
      ctx.font = `700 11px ${font}`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(row.label.toUpperCase(), originX + 12, y + catH / 2);
      y += catH;
      return;
    }

    ctx.fillStyle = colors.marketing.white;
    ctx.fillRect(originX, y, chartW, rowH);

    ctx.strokeStyle = grid;
    ctx.beginPath();
    ctx.moveTo(originX, y);
    ctx.lineTo(originX + chartW, y);
    ctx.stroke();

    const indent = row.kind === 'sub' ? 28 : 12;
    ctx.fillStyle = text;
    ctx.font = `600 12px ${font}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      fitCanvasText(ctx, row.label, labelW - indent - 10),
      originX + indent,
      y + rowH / 2
    );

    const weeks = filledWeeks(ganttSpan(row.item));
    for (let week = 1; week <= GANTT_WEEKS; week += 1) {
      const x = originX + labelW + (week - 1) * weekW;
      ctx.strokeStyle = week % 4 === 1 ? gridStrong : grid;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y + rowH);
      ctx.stroke();
      if (weeks.has(week)) {
        ctx.fillStyle = fill;
        ctx.fillRect(x + 1, y + 1, weekW - 1, rowH - 1);
      }
    }

    y += rowH;
  });

  ctx.strokeStyle = gridStrong;
  ctx.strokeRect(originX + 0.5, originY + 0.5, chartW - 1, headH + bodyH - 1);

  const legendY = y + 22;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.font = `400 12px ${font}`;
  ctx.fillStyle = fill;
  ctx.fillRect(originX + 12, legendY - 7, 14, 14);
  ctx.fillStyle = muted;
  ctx.fillText('On timeline', originX + 32, legendY);
  ctx.strokeStyle = gridStrong;
  ctx.strokeRect(originX + 130, legendY - 7, 14, 14);
  ctx.fillText('No Deadline Available', originX + 150, legendY);

  await new Promise((resolve) => {
    const saveFromDataUrl = () => {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'clickup-gantt.jpg';
      document.body.appendChild(link);
      link.click();
      link.remove();
      resolve();
    };
    if (typeof canvas.toBlob !== 'function') {
      saveFromDataUrl();
      return;
    }
    canvas.toBlob((blob) => {
      if (!blob) {
        saveFromDataUrl();
        return;
      }
      downloadBlob(blob, 'clickup-gantt.jpg');
      resolve();
    }, 'image/jpeg', 0.92);
  });
}

function FieldLabel({ htmlFor, label, required }) {
  return (
    <label htmlFor={htmlFor} className="co-label">
      {label}
      {required ? <span className="co-required">*</span> : null}
    </label>
  );
}

function GanttRow({ label, item, isSub, onRemove }) {
  const span = ganttSpan(item);
  const weeks = filledWeeks(span);
  const title = deadlineLines(item).join(' · ');
  return (
    <div className={`co-gantt-row ${isSub ? 'is-sub' : ''}`}>
      <div className="co-gantt-label">
        <span className="co-gantt-name" title={label}>
          {label}
        </span>
        <button
          type="button"
          className="co-gantt-remove"
          aria-label={`Remove ${label}`}
          onClick={onRemove}
        >
          ×
        </button>
      </div>
      <div className="co-gantt-track" title={title}>
        {Array.from({ length: GANTT_WEEKS }, (_, i) => {
          const week = i + 1;
          return (
            <div
              key={week}
              className={`co-gantt-cell ${weeks.has(week) ? 'is-filled' : ''}`}
            />
          );
        })}
      </div>
    </div>
  );
}

function SelectedGantt({ groups, selectedCount, onRemoveParent, onRemoveSub }) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const downloadJpg = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError('');
    try {
      await exportGanttJpg(groups);
    } catch (err) {
      console.error('Gantt JPG export failed', err);
      setDownloadError('Could not download the Gantt image. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="co-sel-head">
        <span>Selected timeline</span>
        <span className="co-sel-count">{selectedCount}</span>
        <button
          type="button"
          className="co-gantt-download"
          onClick={downloadJpg}
          disabled={downloading}
        >
          {downloading ? 'Preparing JPG...' : 'Download JPG'}
        </button>
      </div>
      {downloadError ? <div className="co-banner">{downloadError}</div> : null}
      <div className="co-gantt-wrap">
        <div className="co-gantt-scroll">
          <div className="co-gantt">
            <div className="co-gantt-axis">
              <div className="co-gantt-corner">
                <div className="co-gantt-corner-title">6-month timeline</div>
              </div>
            <div className="co-gantt-time">
              <div className="co-gantt-months">
                {GANTT_MONTHS.map((month) => (
                  <div key={month} className="co-gantt-month">
                    {month}
                  </div>
                ))}
              </div>
              <div className="co-gantt-weeks">
                {Array.from({ length: GANTT_WEEKS }, (_, i) => (
                  <div key={i + 1} className="co-gantt-week">
                    {i + 1}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {groups.map((group) => (
            <section key={group.category} className="co-gantt-group">
              <div className="co-gantt-cat">{group.category}</div>
              {group.tasks.map(({ task, subtasks }) => (
                <div key={parentKey(task.category, task.task_name)}>
                  <GanttRow
                    label={task.task_name}
                    item={task}
                    onRemove={() => onRemoveParent(task)}
                  />
                  {subtasks.map((sub) => (
                    <GanttRow
                      key={subKey(task.category, task.task_name, sub.task_name)}
                      label={sub.task_name}
                      item={sub}
                      isSub
                      onRemove={() => onRemoveSub(task, sub)}
                    />
                  ))}
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
      <div className="co-gantt-legend">
        <span>
          <i className="co-legend-fill" /> On timeline
        </span>
        <span>
          <i className="co-legend-empty" /> No Deadline Available
        </span>
      </div>
    </div>
    </>
  );
}

function ClickUpTaskPicker({
  catalog,
  catalogError,
  selectedParents,
  selectedSubs,
  onToggleParent,
  onToggleSub,
  onRemoveParent,
  onRemoveSub,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    const items = [];
    for (const task of catalog?.tasks || []) {
      const parentMatch =
        !q ||
        task.task_name.toLowerCase().includes(q) ||
        task.category.toLowerCase().includes(q);
      const matchingSubs = (task.subtasks || []).filter(
        (sub) => !q || sub.task_name.toLowerCase().includes(q)
      );
      if (parentMatch || matchingSubs.length) {
        items.push({ kind: 'parent', task });
        const subsToShow = q ? matchingSubs : task.subtasks || [];
        subsToShow.forEach((sub) => items.push({ kind: 'sub', task, sub }));
      }
    }
    return items;
  }, [catalog, query]);

  const grouped = useMemo(() => {
    const groups = [];
    const seen = new Map();
    for (const item of options) {
      const category = item.task.category;
      if (!seen.has(category)) {
        const list = [];
        seen.set(category, list);
        groups.push({ category, items: list });
      }
      seen.get(category).push(item);
    }
    return groups;
  }, [options]);

  const selectedGroups = useMemo(() => {
    const groups = [];
    const seen = new Map();
    for (const task of catalog?.tasks || []) {
      const key = parentKey(task.category, task.task_name);
      if (!selectedParents.has(key)) continue;
      if (!seen.has(task.category)) {
        const list = [];
        seen.set(task.category, list);
        groups.push({ category: task.category, tasks: list });
      }
      seen.get(task.category).push({
        task,
        subtasks: (task.subtasks || []).filter((sub) =>
          selectedSubs.has(subKey(task.category, task.task_name, sub.task_name))
        ),
      });
    }
    return groups;
  }, [catalog, selectedParents, selectedSubs]);

  const selectedCount = selectedGroups.reduce(
    (sum, group) => sum + group.tasks.length + group.tasks.reduce((n, row) => n + row.subtasks.length, 0),
    0
  );

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const selectItem = (item) => {
    if (item.kind === 'parent') {
      const key = parentKey(item.task.category, item.task.task_name);
      onToggleParent(item.task, !selectedParents.has(key));
    } else {
      const sk = subKey(item.task.category, item.task.task_name, item.sub.task_name);
      onToggleSub(item.task, item.sub, !selectedSubs.has(sk));
    }
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((i) => Math.min(i + 1, Math.max(options.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (options[highlight]) selectItem(options[highlight]);
    }
  };

  const isSelected = (item) => {
    if (item.kind === 'parent') {
      return selectedParents.has(parentKey(item.task.category, item.task.task_name));
    }
    return selectedSubs.has(
      subKey(item.task.category, item.task.task_name, item.sub.task_name)
    );
  };

  let optionIndex = -1;

  return (
    <div className="co-picker" ref={wrapRef}>
      {catalogError ? <div className="co-banner">{catalogError}</div> : null}

      <FieldLabel htmlFor="clickup-search" label="Search deliverables" />
      <div className={`co-combobox ${open ? 'is-open' : ''}`}>
        <svg className="co-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
          <path d="M20 20L16.5 16.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
        <input
          id="clickup-search"
          ref={inputRef}
          className="co-input co-combobox-input"
          value={query}
          placeholder="Search by deliverable or category..."
          autoComplete="off"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {query ? (
          <button
            type="button"
            className="co-clear"
            aria-label="Clear search"
            onClick={() => {
              setQuery('');
              inputRef.current?.focus();
            }}
          >
            ×
          </button>
        ) : null}
      </div>

      {open ? (
        <div className="co-menu" role="listbox">
          {grouped.length === 0 ? (
            <div className="co-menu-empty">No deliverables match that search.</div>
          ) : (
            grouped.map((group) => (
              <div key={group.category} className="co-menu-group">
                <div className="co-menu-heading">{group.category}</div>
                {group.items.map((item) => {
                  optionIndex += 1;
                  const idx = optionIndex;
                  const selected = isSelected(item);
                  const label = item.kind === 'parent' ? item.task.task_name : item.sub.task_name;
                  const metaItem = item.kind === 'parent' ? item.task : item.sub;
                  const recurring = Boolean(metaItem.recurring);
                  return (
                    <button
                      key={item.kind === 'parent' ? parentKey(item.task.category, item.task.task_name) : subKey(item.task.category, item.task.task_name, item.sub.task_name)}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`co-option ${item.kind === 'sub' ? 'is-sub' : ''} ${selected ? 'is-selected' : ''} ${idx === highlight ? 'is-active' : ''}`}
                      onMouseEnter={() => setHighlight(idx)}
                      onClick={() => selectItem(item)}
                    >
                      <span className="co-option-check" aria-hidden>
                        {selected ? '✓' : ''}
                      </span>
                      <span className="co-option-body">
                        <span className="co-option-title">
                          {label}
                          {recurring ? <span className="co-badge">Recurring</span> : null}
                        </span>
                        <DeadlineMeta item={metaItem} compact />
                      </span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      ) : null}

      {selectedGroups.length === 0 ? (
        <>
          <div className="co-sel-head">
            <span>Selected timeline</span>
            <span className="co-sel-count">0</span>
          </div>
          <p className="co-empty">No deliverables selected yet. Search above and add what this client needs. The 6-month Gantt will appear here.</p>
        </>
      ) : (
        <SelectedGantt
          groups={selectedGroups}
          selectedCount={selectedCount}
          onRemoveParent={onRemoveParent}
          onRemoveSub={onRemoveSub}
        />
      )}
    </div>
  );
}

function ClientOnboarding() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState('');
  const [selectedParents, setSelectedParents] = useState(() => new Set());
  const [selectedSubs, setSelectedSubs] = useState(() => new Set());
  const [focused, setFocused] = useState(null);
  const contentRef = useRef(null);
  const autoSellKeyRef = useRef('');
  const [sellGenerating, setSellGenerating] = useState(false);
  const [sellError, setSellError] = useState('');

  useEffect(() => {
    const id = 'co-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;500;600;700;800&display=swap';
      document.head.appendChild(link);
    }
    document.title = 'Client Onboarding | 180 Marketing';
  }, []);

  useEffect(() => {
    api
      .get('/forms/client-onboarding/tasks/')
      .then((res) => {
        setCatalog(res.data);
        setCatalogError('');
      })
      .catch(() => {
        setCatalogError('Could not load deliverables. You can still submit the form.');
      });
  }, []);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const setField = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const generateTheySell = useCallback((opts = {}) => {
    const company = String(opts.company ?? form['Company Name'] ?? '').trim();
    const website = String(opts.website ?? form['Website URL'] ?? '').trim();
    const blog = String(opts.blog ?? form['Website Blog URL'] ?? '').trim();
    if (!company && !website) {
      setSellError('Enter a company name or website URL first.');
      return;
    }
    setSellGenerating(true);
    setSellError('');
    api
      .post(
        '/forms/client-onboarding/suggest-they-sell/',
        {
          'Company Name': company,
          'Website URL': website,
          'Website Blog URL': blog,
        },
        { timeout: 100000 }
      )
      .then((res) => {
        const text = String(res.data?.they_sell || '').trim();
        if (!text) {
          setSellError('Could not generate what they sell. Please try again.');
          return;
        }
        setField('They sell:', text);
      })
      .catch((err) => {
        setSellError(
          formatApiErrors(err?.response?.data) ||
            'Could not generate what they sell. Please try again.'
        );
      })
      .finally(() => setSellGenerating(false));
  }, [form, setField]);

  useEffect(() => {
    if (step !== 1) return;
    const company = String(form['Company Name'] || '').trim();
    const website = String(form['Website URL'] || '').trim();
    if (!company && !website) return;
    if (String(form['They sell:'] || '').trim()) return;
    const key = `${company}|${website}`;
    if (autoSellKeyRef.current === key) return;
    autoSellKeyRef.current = key;
    generateTheySell({
      company,
      website,
      blog: String(form['Website Blog URL'] || '').trim(),
    });
  }, [form, generateTheySell, step]);

  const toggleListValue = (name, option) => {
    const current = Array.isArray(form[name]) ? form[name] : [];
    const next = current.includes(option)
      ? current.filter((item) => item !== option)
      : [...current, option];
    setField(name, next);
  };

  const toggleParent = (task, checked) => {
    const key = parentKey(task.category, task.task_name);
    setSelectedParents((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
    setSelectedSubs((prev) => {
      const next = new Set(prev);
      for (const sub of task.subtasks || []) {
        const sk = subKey(task.category, task.task_name, sub.task_name);
        if (checked) next.add(sk);
        else next.delete(sk);
      }
      return next;
    });
  };

  const toggleSub = (task, sub, checked) => {
    const key = parentKey(task.category, task.task_name);
    const sk = subKey(task.category, task.task_name, sub.task_name);
    setSelectedSubs((prev) => {
      const next = new Set(prev);
      if (checked) next.add(sk);
      else next.delete(sk);
      return next;
    });
    if (checked) {
      setSelectedParents((prev) => new Set(prev).add(key));
    }
  };

  const removeParent = (task) => toggleParent(task, false);
  const removeSub = (task, sub) => toggleSub(task, sub, false);

  const buildSelectedTasks = () => {
    const selected = [];
    for (const task of catalog?.tasks || []) {
      const key = parentKey(task.category, task.task_name);
      if (!selectedParents.has(key)) continue;
      selected.push({
        category: task.category,
        task_name: task.task_name,
        subtasks: (task.subtasks || [])
          .filter((sub) =>
            selectedSubs.has(subKey(task.category, task.task_name, sub.task_name))
          )
          .map((sub) => ({ task_name: sub.task_name })),
      });
    }
    return selected;
  };

  const getStepErrors = (index) => {
    const next = {};
    for (const name of STEPS[index].fields) {
      if (isEmpty(form[name])) next[name] = 'This field is required.';
    }
    if (
      STEPS[index].fields.includes('Client Email') &&
      form['Client Email'] &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form['Client Email'])
    ) {
      next['Client Email'] = 'Enter a valid email address.';
    }
    return next;
  };

  const goToStep = (index) => {
    setSubmitError('');
    setStep(Math.max(0, Math.min(index, STEPS.length - 1)));
  };

  const goNext = () => goToStep(step + 1);
  const goBack = () => goToStep(step - 1);

  const onSubmit = (e) => {
    e.preventDefault();
    if (submitting || submitted) return;
    if (step < STEPS.length - 1) {
      goNext();
      return;
    }

    const allErrors = {};
    let firstInvalid = -1;
    for (let i = 0; i < STEPS.length; i += 1) {
      const stepErrors = getStepErrors(i);
      Object.assign(allErrors, stepErrors);
      if (firstInvalid < 0 && Object.keys(stepErrors).length) firstInvalid = i;
    }
    if (firstInvalid >= 0) {
      setErrors(allErrors);
      setStep(firstInvalid);
      setSubmitError('Please fill in all required fields before submitting.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    const payload = {
      ...form,
      'Monthly Fee': form['Monthly Fee'] === '' ? null : form['Monthly Fee'],
      selected_clickup_tasks: buildSelectedTasks(),
    };

    api
      .post('/forms/client-onboarding/submit/', payload)
      .then(() => {
        setSubmitted(true);
        setSubmitting(false);
      })
      .catch((err) => {
        setSubmitError(formatApiErrors(err?.response?.data));
        setSubmitting(false);
      });
  };

  const fieldClass = (name) => {
    const bits = ['co-input'];
    if (errors[name]) bits.push('has-error');
    if (focused === name) bits.push('is-focused');
    return bits.join(' ');
  };

  const textField = ({ name, label, required, type = 'text', placeholder }) => (
    <div className="co-field">
      <FieldLabel htmlFor={name} label={label} required={required} />
      <input
        id={name}
        type={type}
        value={form[name]}
        placeholder={placeholder}
        onChange={(e) => setField(name, e.target.value)}
        onFocus={() => setFocused(name)}
        onBlur={() => setFocused(null)}
        className={fieldClass(name)}
      />
      {errors[name] ? <div className="co-error">{errors[name]}</div> : null}
    </div>
  );

  const selectField = ({ name, label, required, options, placeholder }) => (
    <div className="co-field">
      <FieldLabel htmlFor={name} label={label} required={required} />
      <div className="co-select-wrap">
        <select
          id={name}
          value={form[name]}
          onChange={(e) => setField(name, e.target.value)}
          onFocus={() => setFocused(name)}
          onBlur={() => setFocused(null)}
          className={fieldClass(name)}
        >
          <option value="">{placeholder || 'Select an option...'}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      {errors[name] ? <div className="co-error">{errors[name]}</div> : null}
    </div>
  );

  const pillGroup = ({ name, label, required, options }) => (
    <div className="co-field">
      <FieldLabel label={label} required={required} />
      <div className="co-pills">
        {options.map((option) => {
          const active = form[name].includes(option);
          return (
            <button
              key={option}
              type="button"
              className={`co-pill ${active ? 'is-on' : ''}`}
              onClick={() => toggleListValue(name, option)}
            >
              {option}
            </button>
          );
        })}
      </div>
      {errors[name] ? <div className="co-error">{errors[name]}</div> : null}
    </div>
  );

  const current = STEPS[step];
  const nextStep = STEPS[step + 1];

  return (
    <div className="co-page">
      <style>{CSS}</style>
      <form className="co-shell" onSubmit={onSubmit} noValidate>
        <aside className="co-sidebar">
          <div>
            <div className="co-brand">180 Marketing</div>
            <div className="co-brand-sub">Client Onboarding</div>
          </div>
          <nav className="co-steps" aria-label="Form sections">
            {STEPS.map((item, index) => {
              const hasRequiredFields = item.fields.length > 0;
              const complete =
                hasRequiredFields && Object.keys(getStepErrors(index)).length === 0;
              const state =
                index === step ? 'is-current' : complete ? 'is-done' : '';
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`co-step ${state}`}
                  onClick={() => goToStep(index)}
                >
                  <span className="co-step-num">{index + 1}</span>
                  <span className="co-step-label">{item.nav}</span>
                </button>
              );
            })}
          </nav>
          <div className="co-step-meta">
            Step {step + 1} of {STEPS.length}
          </div>
        </aside>

        <div className="co-main" ref={contentRef}>
          {submitted ? (
            <div className="co-thanks">
              <p className="co-kicker">Done</p>
              <h1 className="co-h1">Submitted</h1>
              <p className="co-lede">
                Thanks. The onboarding details for this client have been sent.
                Refresh the page if you need to submit another client.
              </p>
            </div>
          ) : (
            <>
              <header className="co-header">
                <p className="co-kicker">{current.kicker}</p>
                <h1 className="co-h1">{current.title}</h1>
                <p className="co-lede">{current.description}</p>
              </header>

              {submitError ? <div className="co-banner">{submitError}</div> : null}

              {step === 0 && (
                <div className="co-grid">
                  <div className="co-span-2">
                    {textField({
                      name: 'Company Name',
                      label: 'Company Name',
                      required: true,
                      placeholder: "Client's Company Name",
                    })}
                  </div>
                  <div className="co-span-2">
                    {textField({
                      name: 'Business Address',
                      label: "Client's Business Address",
                      required: true,
                      placeholder: 'Street, city, state/province, postal code, country',
                    })}
                  </div>
                  {textField({
                    name: 'First Name',
                    label: "Client's First Name",
                    required: true,
                    placeholder: 'John',
                  })}
                  {textField({
                    name: 'Last Name',
                    label: "Client's Last Name",
                    required: true,
                    placeholder: 'Smith',
                  })}
                  {textField({
                    name: 'Client Email',
                    label: "Client's Email",
                    required: true,
                    type: 'email',
                    placeholder: 'name@company.com',
                  })}
                  {textField({
                    name: 'Website URL',
                    label: 'Website URL',
                    required: true,
                    placeholder: 'https://clientdomain.com/',
                  })}
                  {textField({
                    name: 'Website Blog URL',
                    label: "Client's Website Blog URL",
                    required: true,
                    placeholder: 'https://clientdomain.com/blog/',
                  })}
                  {textField({
                    name: 'Start Date',
                    label: "Client's Start Date",
                    required: true,
                    type: 'date',
                  })}
                  {selectField({
                    name: 'Project Manager',
                    label: 'Project Manager',
                    required: true,
                    options: PROJECT_MANAGERS,
                  })}
                </div>
              )}

              {step === 1 && (
                <div className="co-grid">
                  <div className="co-span-2">
                    {pillGroup({
                      name: 'Service Type/s',
                      label: 'Service Type/s',
                      required: true,
                      options: SERVICE_TYPES,
                    })}
                  </div>
                  {selectField({
                    name: 'AI SEO Plan Type',
                    label: 'AI SEO Plan Type',
                    required: true,
                    options: AI_SEO_PLAN_TYPES,
                  })}
                  {selectField({
                    name: 'Fee Type',
                    label: 'Fee Type',
                    required: true,
                    options: FEE_TYPES,
                  })}
                  {textField({
                    name: 'Monthly Fee',
                    label: 'Monthly Fee',
                    type: 'number',
                    placeholder: 'Enter Monthly Fee',
                  })}
                  {selectField({
                    name: 'Primary Market/Region',
                    label: 'Primary Market/Region',
                    required: true,
                    options: MARKETS,
                  })}
                  <div className="co-span-2">
                    {pillGroup({
                      name: 'Secondary Market/Region/s',
                      label: 'Secondary Market/Region/s',
                      options: MARKETS,
                    })}
                  </div>
                  <div className="co-span-2">
                    <div className="co-field">
                      <div className="co-label-row">
                        <FieldLabel htmlFor="They sell:" label="What They Sell" required />
                        <button
                          type="button"
                          className="co-ai-btn"
                          onClick={() => generateTheySell()}
                          disabled={sellGenerating}
                        >
                          {sellGenerating ? 'Generating...' : form['They sell:'] ? 'Regenerate with AI' : 'Generate with AI'}
                        </button>
                      </div>
                      <textarea
                        id="They sell:"
                        value={form['They sell:']}
                        placeholder={
                          sellGenerating
                            ? 'Researching the company website...'
                            : 'What products or services does the client provide?'
                        }
                        onChange={(e) => setField('They sell:', e.target.value)}
                        onFocus={() => setFocused('They sell:')}
                        onBlur={() => setFocused(null)}
                        className={`co-input co-textarea co-textarea-compact ${errors['They sell:'] ? 'has-error' : ''} ${focused === 'They sell:' ? 'is-focused' : ''}`}
                        rows={4}
                        disabled={sellGenerating}
                      />
                      {sellError ? <div className="co-error">{sellError}</div> : null}
                      {errors['They sell:'] ? <div className="co-error">{errors['They sell:']}</div> : null}
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="co-grid">
                  {textField({
                    name: 'Start Campaign Quota Date',
                    label: 'Start Campaign Quota Date',
                    required: true,
                    type: 'date',
                  })}
                  {textField({
                    name: 'Monthly Campaign Quota',
                    label: 'Monthly Campaign Quota',
                    required: true,
                    type: 'number',
                    placeholder: '10',
                  })}
                  {textField({
                    name: 'Outreach Emails Per Weekday',
                    label: 'Outreach Emails Per Weekday',
                    required: true,
                    type: 'number',
                    placeholder: '10',
                  })}
                  {textField({
                    name: 'Monthly Page Optimizations',
                    label: 'Monthly Page Optimizations',
                    required: true,
                    type: 'number',
                    placeholder: '10',
                  })}
                  {textField({
                    name: 'Monthly Blog Posts',
                    label: 'Monthly Blog Posts',
                    required: true,
                    type: 'number',
                    placeholder: '10',
                  })}
                  {textField({
                    name: 'Reddit Quota',
                    label: 'Reddit Quota / Month',
                    required: true,
                    type: 'number',
                    placeholder: '20',
                  })}
                </div>
              )}

              {step === 3 && (
                <ClickUpTaskPicker
                  catalog={catalog}
                  catalogError={catalogError}
                  selectedParents={selectedParents}
                  selectedSubs={selectedSubs}
                  onToggleParent={toggleParent}
                  onToggleSub={toggleSub}
                  onRemoveParent={removeParent}
                  onRemoveSub={removeSub}
                />
              )}

              {step === 4 && (
                <div className="co-field">
                  <FieldLabel htmlFor="Notes" label="Notes" required />
                  <textarea
                    id="Notes"
                    value={form.Notes}
                    placeholder="Add any special needs, requirements, exceptions, or additional commentary."
                    onChange={(e) => setField('Notes', e.target.value)}
                    onFocus={() => setFocused('Notes')}
                    onBlur={() => setFocused(null)}
                    className={`co-input co-textarea ${errors.Notes ? 'has-error' : ''}`}
                    rows={8}
                  />
                  {errors.Notes ? <div className="co-error">{errors.Notes}</div> : null}
                </div>
              )}

              {nextStep && step < STEPS.length - 1 ? (
                <div className="co-peek">
                  <p className="co-kicker">{nextStep.kicker}</p>
                  <p className="co-peek-title">{nextStep.title}</p>
                </div>
              ) : null}

              <div className="co-actions">
                <button
                  type="button"
                  className="co-btn co-btn-ghost"
                  onClick={goBack}
                  disabled={step === 0}
                >
                  Back
                </button>
                {step < STEPS.length - 1 ? (
                  <button type="submit" className="co-btn co-btn-primary">
                    Continue
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="co-btn co-btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

const m = colors.marketing;

const CSS = `
.co-page {
  --m-green: ${m.green};
  --m-green-secondary: ${m.greenSecondary};
  --m-green-hover: ${m.greenHover};
  --m-charcoal: ${m.charcoal};
  --m-deep-charcoal: ${m.deepCharcoal};
  --m-grey-light: ${m.greyLight};
  --m-grey-muted: ${m.greyMuted};
  --m-border: ${m.borderGrey};
  --m-border-dark: ${m.borderDark};
  --m-bg: ${m.background};
  --m-green-soft: ${m.greenSoft};
  --m-white: ${m.white};
  --m-pure-white: ${m.pureWhite};
  --m-danger: ${colors.text.danger};
  height: 100vh;
  overflow: hidden;
  background: var(--m-bg);
  font-family: 'Open Sans', Arial, sans-serif;
  color: var(--m-deep-charcoal);
  padding: 0;
}
.co-shell {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  width: 100%;
  height: 100%;
  min-height: 0;
  margin: 0;
  background: var(--m-white);
  border-radius: 0;
  overflow: hidden;
}
.co-sidebar {
  display: flex;
  flex-direction: column;
  gap: 36px;
  padding: 36px 28px;
  background: var(--m-green-soft);
  height: 100%;
  min-height: 0;
  overflow: auto;
  position: sticky;
  top: 0;
}
.co-brand {
  font-family: 'Open Sans', Arial, sans-serif;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.4px;
  color: var(--m-deep-charcoal);
  line-height: 1.1;
}
.co-brand-sub {
  margin-top: 6px;
  font-size: 14px;
  color: var(--m-grey-light);
}
.co-steps {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.co-step {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  color: var(--m-charcoal);
  border-radius: 999px;
  padding: 10px 12px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  transition: background 180ms ease, color 180ms ease;
}
.co-step:hover { background: var(--m-bg); }
.co-step.is-current {
  background: var(--m-green);
  color: var(--m-deep-charcoal);
}
.co-step.is-done { color: var(--m-deep-charcoal); }
.co-step-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  background: var(--m-pure-white);
}
.co-step.is-current .co-step-num { background: var(--m-pure-white); }
.co-step-meta {
  margin-top: auto;
  font-size: 13px;
  color: var(--m-grey-light);
}
.co-main {
  padding: 48px 64px 40px;
  min-height: 0;
  height: 100%;
  overflow: auto;
}
.co-kicker {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--m-green-hover);
}
.co-h1 {
  margin: 0 0 10px;
  font-family: 'Open Sans', Arial, sans-serif;
  font-size: 36px;
  font-weight: 800;
  letter-spacing: -0.5px;
  line-height: 1.15;
  color: var(--m-deep-charcoal);
}
.co-lede {
  margin: 0 0 28px;
  max-width: none;
  font-size: 15px;
  line-height: 1.55;
  color: var(--m-grey-light);
}
.co-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px 24px;
}
.co-span-2 { grid-column: 1 / -1; }
.co-field { min-width: 0; }
.co-label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--m-charcoal);
}
.co-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.co-label-row .co-label { margin-bottom: 0; }
.co-ai-btn {
  flex-shrink: 0;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1.5px solid var(--m-green);
  background: var(--m-green);
  color: var(--m-deep-charcoal);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.co-ai-btn:hover { background: var(--m-green-hover); border-color: var(--m-green-hover); }
.co-ai-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.co-textarea-compact { min-height: 110px; }
.co-input:disabled, .co-textarea:disabled {
  opacity: 0.7;
  cursor: wait;
}
.co-required { color: var(--m-danger); margin-left: 4px; }
.co-input, .co-textarea, .co-select-wrap select {
  width: 100%;
  border: 1px solid var(--m-border);
  border-radius: 14px;
  background: var(--m-bg);
  padding: 14px 16px;
  font-family: inherit;
  font-size: 14px;
  color: var(--m-deep-charcoal);
  outline: none;
  transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
}
.co-input:hover, .co-textarea:hover { background: var(--m-green-soft); }
.co-input.is-focused, .co-input:focus, .co-textarea:focus, .co-combobox.is-open .co-combobox-input {
  border-color: var(--m-green);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--m-green) 22%, transparent);
  background: var(--m-pure-white);
}
.co-input.has-error, .co-textarea.has-error {
  border-color: var(--m-danger);
  background: color-mix(in srgb, var(--m-danger) 8%, var(--m-white));
}
.co-input::placeholder, .co-textarea::placeholder {
  color: var(--m-grey-muted);
}
.co-select-wrap { position: relative; }
.co-select-wrap select { appearance: none; padding-right: 40px; }
.co-select-wrap::after {
  content: '';
  position: absolute;
  right: 16px;
  top: 50%;
  width: 8px;
  height: 8px;
  border-right: 1.6px solid var(--m-charcoal);
  border-bottom: 1.6px solid var(--m-charcoal);
  transform: translateY(-70%) rotate(45deg);
  pointer-events: none;
}
.co-error { margin-top: 6px; font-size: 12px; color: var(--m-danger); }
.co-pills { display: flex; flex-wrap: wrap; gap: 8px; }
.co-pill {
  border: 1px solid var(--m-border);
  background: var(--m-pure-white);
  color: var(--m-deep-charcoal);
  border-radius: 999px;
  padding: 9px 14px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  transition: background 160ms ease, border-color 160ms ease, color 160ms ease;
}
.co-pill:hover { border-color: var(--m-green); }
.co-pill.is-on {
  background: var(--m-green);
  border-color: var(--m-green);
  color: var(--m-deep-charcoal);
}
.co-textarea { resize: vertical; min-height: 180px; line-height: 1.55; }
.co-banner {
  margin-bottom: 20px;
  padding: 12px 14px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--m-danger) 10%, var(--m-white));
  color: var(--m-danger);
  font-size: 14px;
}
.co-peek {
  margin-top: 40px;
  padding-top: 22px;
  border-top: 1px solid var(--m-border);
  opacity: 0.45;
}
.co-peek-title {
  margin: 0;
  font-family: 'Open Sans', Arial, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: var(--m-deep-charcoal);
}
.co-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 36px;
}
.co-btn {
  min-width: 112px;
  height: 46px;
  padding: 0 22px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  transition: background 160ms ease, border-color 160ms ease, opacity 160ms ease;
}
.co-btn-ghost {
  background: var(--m-pure-white);
  border: 1.5px solid var(--m-deep-charcoal);
  color: var(--m-deep-charcoal);
}
.co-btn-ghost:disabled { opacity: 0.35; cursor: not-allowed; }
.co-btn-primary {
  background: var(--m-green);
  border: 1.5px solid var(--m-green);
  color: var(--m-deep-charcoal);
}
.co-btn-primary:hover { background: var(--m-green-hover); border-color: var(--m-green-hover); }
.co-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.co-thanks { padding-top: 48px; max-width: 520px; }
.co-picker { position: relative; }
.co-combobox {
  position: relative;
  display: flex;
  align-items: center;
}
.co-search-icon {
  position: absolute;
  left: 16px;
  color: var(--m-grey-light);
  pointer-events: none;
}
.co-combobox-input { padding-left: 44px; padding-right: 40px; }
.co-clear {
  position: absolute;
  right: 10px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: var(--m-grey-muted);
}
.co-menu {
  position: absolute;
  z-index: 8;
  left: 0;
  right: 0;
  margin-top: 8px;
  max-height: 340px;
  overflow: auto;
  background: var(--m-pure-white);
  border: 1px solid var(--m-border);
  border-radius: 16px;
  box-shadow: 0 16px 40px color-mix(in srgb, var(--m-deep-charcoal) 14%, transparent);
}
.co-menu-empty, .co-empty {
  padding: 16px;
  font-size: 14px;
  color: var(--m-grey-muted);
}
.co-empty { padding: 8px 0 0; }
.co-menu-heading {
  padding: 10px 14px 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--m-green-hover);
}
.co-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 9px 14px;
  font-family: inherit;
  color: inherit;
}
.co-option.is-sub { padding-left: 36px; }
.co-option.is-active { background: var(--m-green-soft); }
.co-option.is-selected { color: var(--m-deep-charcoal); }
.co-option-check {
  width: 16px;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--m-green-hover);
}
.co-option-title { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 500; }
.co-option-meta { display: block; margin-top: 3px; font-size: 12px; line-height: 1.4; color: var(--m-grey-muted); }
.co-badge {
  display: inline-flex;
  padding: 1px 7px;
  border-radius: 6px;
  background: var(--m-green-soft);
  color: var(--m-charcoal);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.co-sel-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 28px 0 14px;
  font-size: 13px;
  font-weight: 600;
}
.co-gantt-download {
  margin-left: auto;
  height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1.5px solid var(--m-green);
  background: var(--m-green);
  color: var(--m-deep-charcoal);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
}
.co-gantt-download:disabled { opacity: 0.6; cursor: not-allowed; }
.co-sel-count {
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 6px;
  background: var(--m-green-soft);
  color: var(--m-charcoal);
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.co-gantt-wrap {
  border: 1px solid var(--m-border);
  border-radius: 16px;
  background: var(--m-pure-white);
  overflow: hidden;
}
.co-gantt-scroll { overflow-x: auto; }
.co-gantt { min-width: 920px; }
.co-gantt-axis, .co-gantt-row {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  align-items: stretch;
}
.co-gantt-axis {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--m-green-soft);
  border-bottom: 1px solid var(--m-border);
}
.co-gantt-corner {
  padding: 10px 12px 8px;
  border-right: 1px solid var(--m-border);
  display: flex;
  align-items: center;
}
.co-gantt-corner-title { font-size: 12px; font-weight: 700; }
.co-gantt-time { min-width: 0; }
.co-gantt-months, .co-gantt-weeks {
  display: grid;
  grid-template-columns: repeat(24, minmax(0, 1fr));
}
.co-gantt-month {
  grid-column: span 4;
  padding: 8px 4px 2px;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--m-charcoal);
  border-left: 1px solid var(--m-border);
}
.co-gantt-week {
  padding: 2px 0 8px;
  text-align: center;
  font-size: 10px;
  color: var(--m-grey-muted);
  border-left: 1px solid var(--m-border);
}
.co-gantt-cat {
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--m-charcoal);
  background: var(--m-green-soft);
}
.co-gantt-row { border-top: 1px solid var(--m-border); }
.co-gantt-label {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px 0 12px;
  min-height: 38px;
  border-right: 1px solid var(--m-border);
  background: var(--m-white);
}
.co-gantt-row.is-sub .co-gantt-label { padding-left: 28px; }
.co-gantt-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
}
.co-gantt-remove {
  border: none;
  background: transparent;
  color: var(--m-grey-muted);
  font-size: 16px;
  line-height: 1;
  padding: 0;
}
.co-gantt-remove:hover { color: var(--m-danger); }
.co-gantt-track {
  display: grid;
  grid-template-columns: repeat(24, minmax(0, 1fr));
  min-height: 38px;
}
.co-gantt-cell {
  border-left: 1px solid var(--m-border);
  background: transparent;
}
.co-gantt-cell:nth-child(4n + 1) {
  border-left-color: var(--m-border-dark);
}
.co-gantt-cell.is-filled {
  background: var(--m-green);
}
.co-gantt-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 10px 14px;
  border-top: 1px solid var(--m-border);
  font-size: 12px;
  color: var(--m-grey-light);
}
.co-gantt-legend span { display: inline-flex; align-items: center; gap: 8px; }
.co-legend-fill, .co-legend-empty {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 1px solid var(--m-border);
}
.co-legend-fill { background: var(--m-green); border-color: var(--m-green); }
.co-legend-empty { background: transparent; }
.co-page input[type='number']::-webkit-outer-spin-button,
.co-page input[type='number']::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.co-page input[type='number'] { -moz-appearance: textfield; }
@media (max-width: 900px) {
  .co-page { height: auto; min-height: 100vh; overflow: visible; padding: 0; }
  .co-shell { grid-template-columns: 1fr; height: auto; min-height: 100vh; }
  .co-sidebar { height: auto; position: static; gap: 18px; padding: 22px; }
  .co-steps { flex-direction: row; overflow-x: auto; }
  .co-step { white-space: nowrap; }
  .co-step-meta { margin-top: 0; }
  .co-main { height: auto; overflow: visible; padding: 28px 22px 24px; }
  .co-h1 { font-size: 32px; }
  .co-grid { grid-template-columns: 1fr; }
}
`;

export default ClientOnboarding;
